import React, {useEffect, useState} from 'react'
import styled from 'styled-components'
import Dialog from 'lib/ui/Dialog'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import Button from 'lib/ui/Button'
import {Emoji, emojiWithName} from 'Event/Dashboard/components/EmojiList/emoji'
import {colors, spacing} from 'lib/ui/theme'

export default function SelectDemoEmojisDialog(props: {
  open: boolean
  availableEmojis: string[]
  onClose: () => void
  onConfirm: (selected: string[]) => void
}) {
  const {open, availableEmojis, onClose, onConfirm} = props
  const [selected, setSelected] = useState<string[]>([])

  useEffect(() => {
    if (open) {
      setSelected(availableEmojis)
    }
  }, [open, availableEmojis])

  const isSelected = (name: string) => selected.includes(name)

  const toggle = (name: string) => {
    setSelected((current) =>
      current.includes(name)
        ? current.filter((emoji) => emoji !== name)
        : [...current, name],
    )
  }

  const selectAll = () => {
    setSelected(availableEmojis)
  }

  const selectNone = () => {
    setSelected([])
  }

  const confirm = () => {
    onConfirm(selected)
  }

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Select emojis for demo</DialogTitle>
      <DialogContent>
        <Info>Choose which emojis to send during the demo.</Info>
        <Toolbar>
          <SelectionCount>
            {selected.length} of {availableEmojis.length} selected
          </SelectionCount>
          <ToolbarActions>
            <TextButton type="button" onClick={selectAll}>
              Select all
            </TextButton>
            <TextButton type="button" onClick={selectNone}>
              Select none
            </TextButton>
          </ToolbarActions>
        </Toolbar>
        <EmojiGrid>
          {availableEmojis.map((name) => (
            <DemoEmoji
              key={name}
              name={name}
              selected={isSelected(name)}
              onToggle={toggle}
            />
          ))}
        </EmojiGrid>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="warning"
          aria-label="start emoji demo"
          disabled={selected.length === 0}
          onClick={confirm}
        >
          Start Demo
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function DemoEmoji(props: {
  name: string
  selected: boolean
  onToggle: (name: string) => void
}) {
  const {name, selected, onToggle} = props
  const emoji = emojiWithName(name)

  return (
    <EmojiTile
      type="button"
      selected={selected}
      aria-label={`${selected ? 'unselect' : 'select'} ${name} emoji for demo`}
      onClick={() => onToggle(name)}
    >
      <EmojiLabel emoji={emoji} />
    </EmojiTile>
  )
}

function EmojiLabel(props: {emoji: Emoji}) {
  const {emoji} = props

  if (emoji.icon) {
    return <EmojiIcon>{emoji.icon}</EmojiIcon>
  }

  if (emoji.image) {
    return <EmojiImage src={emoji.image} alt={emoji.name} />
  }

  return <EmojiName>{emoji.name}</EmojiName>
}

const Info = styled.p`
  margin: 0 0 ${spacing[3]};
  color: ${colors.text.info};
  font-size: 14px;
`

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing[3]};
  margin-bottom: ${spacing[3]};
  padding-bottom: ${spacing[3]};
  border-bottom: 1px solid ${colors.gray300};
`

const SelectionCount = styled.span`
  color: ${colors.text.muted};
  font-size: 13px;
`

const ToolbarActions = styled.div`
  display: flex;
  gap: ${spacing[3]};
`

const TextButton = styled.button`
  background: none;
  border: 0;
  padding: 0;
  color: ${colors.primary};
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;

  &:hover {
    text-decoration: underline;
  }
`

const EmojiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(56px, 1fr));
  gap: ${spacing[2]};
  max-height: 280px;
  overflow-y: auto;
  padding: ${spacing[1]};
`

const EmojiTile = styled.button<{selected: boolean}>`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 56px;
  padding: ${spacing[2]};
  border: 2px solid
    ${(props) => (props.selected ? colors.primary : colors.gray300)};
  border-radius: 8px;
  background: ${colors.white};
  cursor: pointer;

  &:focus {
    outline: none;
  }
`

const EmojiIcon = styled.span`
  font-size: 28px;
  line-height: 1;
`

const EmojiImage = styled.img`
  height: 32px;
  width: 32px;
  object-fit: contain;
`

const EmojiName = styled.span`
  font-size: 12px;
  color: ${colors.text.primary};
  text-transform: capitalize;
`
